import { computed, type ComputedRef } from 'vue';
import type { Router } from 'vue-router';
import { useUser } from '@/store/user';
import * as selectedBrandStorage from '@lib/selected-brand-storage';
import { intercomShutdown } from '@lib/intercom';

export interface PlmBrand {
  /** Company connection id — the brand itself is `brandID`. */
  id: number;
  brandID: number;
  name: string;
}

/**
 * The brands the user can switch between — PLM-enabled company connections only.
 * Non-PLM connections (suppliers, etc.) have no usable brandId in this UI and would
 * render as disabled junk entries.
 */
export function usePlmBrands(): ComputedRef<PlmBrand[]> {
  return computed(() => useUser().companies.filter((c: any) => c.plm && c.brandID) as PlmBrand[]);
}

/** The brands other than the one currently open. */
export function useOtherPlmBrands(): ComputedRef<PlmBrand[]> {
  const brands = usePlmBrands();
  return computed(() => brands.value.filter((brand) => brand.brandID !== useUser().brandId));
}

let appRouter: Router | undefined;

/** Lets a brand switch find out which page it is leaving. */
export function installBrandSwitch(router: Router) {
  appRouter = router;
}

/**
 * Where a brand switch lands when the caller has nowhere particular in mind: the same page, as long
 * as its URL names nothing that belongs to the brand being left.
 *
 * - The query goes in every case. Filters, the active season and the like name the old brand's data.
 *   The saved view/filters belong to the old brand too, which is why this never goes through the
 *   '/' redirect that re-applies them via ?view=.
 * - An open fly-in (a route's `overlayParam`, as in `/plm/orders/:orderNumber?`) is closed, and the
 *   page under it is kept.
 * - A page that is one entity (`/plm/product/:id`) does not exist in the next brand, so it falls back
 *   to the products list.
 */
export function brandSwitchDestination(router: Router | undefined = appRouter): string {
  const fallback = '/plm/products';
  if (!router) return fallback;

  const { name, params, meta } = router.currentRoute.value;
  if (!name || meta.noAuth) return fallback;

  const overlayParam = meta.overlayParam;
  const namesAnEntity = Object.entries(params).some(
    ([key, value]) => key !== overlayParam && (Array.isArray(value) ? value.length > 0 : !!value)
  );
  if (namesAnEntity) return fallback;

  return router.resolve({ name, params: {} }).path;
}

/**
 * Switch the tab to another brand and reload into `destination`.
 *
 * A caller that knows where the reader wanted to go (a deep link into a product that turned out to
 * live in another brand) passes that path; otherwise see `brandSwitchDestination`. The active season
 * is stored per brand, so it needs no clearing here.
 */
export function switchBrand(brandId: number, destination = brandSwitchDestination()): void {
  selectedBrandStorage.write(brandId);
  // Reset Intercom before the reload re-boots it. The logged-in user is unchanged
  // across a brand switch, so without a shutdown the post-reload boot would restore
  // the previous brand's Messenger session (open conversation, state) from cookies.
  // Shutting down clears those cookies so the new boot starts a clean chat associated
  // with the new brand's company. Mirrors logOut().
  intercomShutdown();
  window.location.assign(destination);
}

export function switchBrandInNewTab(brandId: number) {
  // sessionStorage is copied to a new tab when opened by window.open() (per HTML
  // spec, unlike user-initiated middle/Ctrl-click on a link). Seed it with the
  // target brand so the new tab boots there, then restore the parent tab's value.
  selectedBrandStorage.withSessionSeed(brandId, () => window.open(brandSwitchDestination(), '_blank'));
}

/** Click handling shared by every brand row: modified click opens the brand in a new tab. */
export function onBrandClick(event: MouseEvent, brandId: number) {
  if (!brandId || brandId === useUser().brandId) return;
  if (event.ctrlKey || event.metaKey || event.shiftKey) {
    switchBrandInNewTab(brandId);
    return;
  }
  switchBrand(brandId);
}

export function onBrandMousedown(event: MouseEvent, brandId: number) {
  if (event.button !== 1) return;
  if (!brandId || brandId === useUser().brandId) return;
  event.preventDefault();
  switchBrandInNewTab(brandId);
}
